import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar as SidebarStyled,
  SidebarHeader,
  Logo,
  LogoText,
  LogoIcon,
  CollapseButton,
  SidebarNav,
  NavList,
  NavItem,
  NavLink,
  NavIcon,
  NavLabel,
  SidebarFooter,
  UserInfo,
  UserDetails,
  UserAvatar,
  UserText,
  UserName,
  UserRole,
  UserAvatarCollapsed,
  MobileBottomNav,
  MobileBottomItem,
  MobileMoreOverlay,
  MobileMoreSheet,
  MobileMoreHeader,
  MobileMoreGrid,
  MobileMoreItem,
} from "../styles/SidebarStyles";
import { useDispatch } from "react-redux";
import { ComponentUpdate } from "../store/slices/component";
import { MyAccounts } from "../common/CommonApi";
import { menuItems } from "../common/common";

const Sidebar = ({ collapsed }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [showMore, setShowMore] = useState(false);

  const isPathActive = (currentPath, menuPath) => {
    return (
      currentPath === menuPath || currentPath.startsWith(`${menuPath}/`)
    );
  };

  const handleCollapse = () => {
    dispatch(ComponentUpdate({ isSideBar: !collapsed }));
  };
  useEffect(() => {
    GetMyAccount();
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const handleViewportChange = (event) => {
      setIsMobile(event.matches);
      setShowMore(false);
    };

    setIsMobile(media.matches);
    media.addEventListener("change", handleViewportChange);
    return () => media.removeEventListener("change", handleViewportChange);
  }, []);

  useEffect(() => {
    if (!isMobile || !showMore) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowMore(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMobile, showMore]);
  

  async function GetMyAccount() {
    try {
      const res = await MyAccounts();
      setUser(res.permissions);
    } catch (err) {
      console.error("Error fetching account:", err);
    }
  }
  // 🟢 Show all menus if user not loaded yet (to avoid empty flash)
  // or filter when user is available
  const visibleMenu =
    !user?.length
      ? [] // optional: show all until user loads
      : menuItems.filter((item) =>
          user.some(
            (perm) => perm.toLowerCase() === item.label.toLowerCase()
          )
        );

  const primaryIds = ["dashboard", "products", "orders", "analytics"];
  const primaryMenu = primaryIds
    .map((id) => visibleMenu.find((item) => item.id === id))
    .filter(Boolean);
  const moreMenu = visibleMenu.filter((item) => !primaryIds.includes(item.id));

  // 🚫 Restrict navigation to unauthorized routes
  useEffect(() => {
    if (user?.permissions?.length) {
      const currentPath = location.pathname;
      const currentItem = menuItems.find((m) => m.to === currentPath);

      if (currentItem && !user.permissions.includes(currentItem.label)) {
        // console.log("Hello3")
        navigate("/dashboard");
      }
    }
  }, [location.pathname, user]);

  if (isMobile) {
    const moreIsActive = moreMenu.some((item) =>
      isPathActive(location.pathname, item.to)
    );

    return (
      <>
        <MobileMoreOverlay
          className={showMore ? "visible" : ""}
          onClick={() => setShowMore(false)}
          aria-hidden="true"
          tabIndex={-1}
        />

        <MobileMoreSheet className={showMore ? "visible" : ""} aria-hidden={!showMore}>
          <MobileMoreHeader>
            <strong>More</strong>
            <button type="button" onClick={() => setShowMore(false)} aria-label="Close menu">×</button>
          </MobileMoreHeader>
          <MobileMoreGrid>
            {moreMenu.map((item) => (
              <MobileMoreItem
                as={Link}
                key={item.id}
                to={item.to}
                className={isPathActive(location.pathname, item.to) ? "active" : ""}
                onClick={() => setShowMore(false)}
              >
                <NavIcon>{item.icon}</NavIcon>
                <span>{item.label}</span>
              </MobileMoreItem>
            ))}
          </MobileMoreGrid>
        </MobileMoreSheet>

        <MobileBottomNav aria-label="Main navigation">
          {primaryMenu.map((item) => (
            <MobileBottomItem
              as={Link}
              key={item.id}
              to={item.to}
              className={isPathActive(location.pathname, item.to) ? "active" : ""}
              onClick={() => setShowMore(false)}
            >
              <NavIcon>{item.icon}</NavIcon>
              <span>{item.label}</span>
            </MobileBottomItem>
          ))}
          {moreMenu.length > 0 && (
            <MobileBottomItem
              as="button"
              type="button"
              className={showMore || moreIsActive ? "active" : ""}
              onClick={() => setShowMore((open) => !open)}
              aria-expanded={showMore}
            >
              <NavIcon>•••</NavIcon>
              <span>More</span>
            </MobileBottomItem>
          )}
        </MobileBottomNav>
      </>
    );
  }

  return (
    <SidebarStyled className={collapsed ? "collapsed" : ""}>
      <SidebarHeader>
        <Logo>
          {!collapsed && (
            <LogoText>
              <img src="/assets/logo.png" alt="logo" />
            </LogoText>
          )}
          {collapsed && <LogoIcon>Ootfit</LogoIcon>}
        </Logo>
        <CollapseButton onClick={handleCollapse}>
          {collapsed ? "→" : "←"}
        </CollapseButton>
      </SidebarHeader>

      <SidebarNav>
        <NavList>
          {visibleMenu.map((item) => {
            const isActive = isPathActive(location.pathname, item.to);
            return (
              <NavItem key={item.id}>
                <NavLink
                  as={Link}
                  className={isActive ? "active" : ""}
                  to={item.to}
                  title={collapsed ? item.label : ""}
                  style={{textDecoration:"none"}}
                >
                  <NavIcon>{item.icon}</NavIcon>
                  {!collapsed && <NavLabel>{item.label}</NavLabel>}
                </NavLink>
              </NavItem>
            );
          })}

          {visibleMenu.length === 0 && (
            <NavItem>
              <NavLabel style={{ padding: "10px", opacity: 0.6 }}>
                Loading...
              </NavLabel>
            </NavItem>
          )}
        </NavList>
      </SidebarNav>

      {/* <SidebarFooter>
        {user && (
          <UserInfo>
            {!collapsed ? (
              <UserDetails>
                <UserAvatar>👤</UserAvatar>
                <UserText>
                  <UserName>{user.fullname}</UserName>
                  <UserRole>{user.role}</UserRole>
                </UserText>
              </UserDetails>
            ) : (
              <UserAvatarCollapsed>👤</UserAvatarCollapsed>
            )}
          </UserInfo>
        )}
      </SidebarFooter> */}
    </SidebarStyled>
  );
};

export default Sidebar;
